import type { Session } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { AdminAccess } from "./AdminAccess";
import { PersonalProposalAdmin } from "./PersonalProposalAdmin";
import { getAdminSupabaseClient, readAdminSession, signOutAdmin } from "./lib/adminRepository";
import {
  loadWorkspaceSnapshot,
  type WorkspaceSnapshot,
} from "./lib/adminWorkspaceRepository";
import "./admin-workspace.css";

export function ProposalAdminWorkspaceAccess() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    readAdminSession()
      .then((value) => active && setSession(value))
      .finally(() => active && setLoading(false));
    const { data } = getAdminSupabaseClient().auth.onAuthStateChange((_event, nextSession) => {
      if (active) {
        setSession(nextSession);
        setLoading(false);
      }
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return <div className="workspace-loading">Persönliche Vorschläge werden geöffnet …</div>;
  }
  if (!session) return <AdminAccess />;
  return <ProposalAdminWorkspace />;
}

function ProposalAdminWorkspace() {
  const [snapshot, setSnapshot] = useState<WorkspaceSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      setSnapshot(await loadWorkspaceSnapshot());
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Die Verwaltungsdaten konnten nicht geladen werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: initial workspace load intentionally runs once.
  useEffect(() => {
    void refresh();
  }, []);

  return (
    <main className="workspace-shell">
      <header className="workspace-header">
        <div>
          <p className="workspace-kicker">Rayk Concierge</p>
          <h1>Persönliche Vorschläge</h1>
          <p className="workspace-lead">
            Ausgewählte Menschen bekommen einen persönlichen Rahmen, konkrete Zeiten und einen
            fertigen Link – ohne selbst im Kalender suchen zu müssen.
          </p>
        </div>
        <div className="workspace-header-actions">
          <a className="workspace-secondary-link" href="/?admin=1">
            Angebote & Zeiten
          </a>
          <a className="workspace-secondary-link" href="/?admin=events">
            Veranstaltungen
          </a>
          <button type="button" onClick={() => void refresh()} disabled={busy}>
            Aktualisieren
          </button>
          <button type="button" onClick={() => void signOutAdmin()}>
            Abmelden
          </button>
        </div>
      </header>

      {error ? <div className="workspace-notice error">{error}</div> : null}
      {!snapshot ? (
        <section className="workspace-panel">
          <p>{busy ? "Daten werden geladen …" : "Keine Daten verfügbar."}</p>
        </section>
      ) : (
        <PersonalProposalAdmin snapshot={snapshot} />
      )}
    </main>
  );
}
